import React, { useEffect, useRef } from 'react';

import { cn } from '../../../../../lib/utils';
import { CPT_TASK_LOG_TONE, CptViewTone } from './constants';

interface CptCodesViewProps {
    value: any[];
    sumField: string;
    label?: string;
    onCodesChange?: (codes: CptCode[]) => void;
    tone?: CptViewTone;
}

export interface CptCode {
    code: string;
    label: string;
}

export const calculateCptCodes = (total: number): CptCode[] => {
    // Calculate applicable codes based on time thresholds
    // >= 8 min: Code 14 (max 1)
    // >= 31 min: Code 15 (first)
    // >= 61 min: Code 15 (second)
    // >= 91 min: Code 15 (third)
    // Cap at 1x Code 14 and 3x Code 15
    const codes: CptCode[] = [];

    if (total >= 8) {
        codes.push({ code: '76014', label: 'CPT 76014' });
    }
    if (total >= 31) {
        codes.push({ code: '76015', label: 'CPT 76015' });
    }
    if (total >= 61) {
        codes.push({ code: '76015', label: 'CPT 76015' });
    }
    if (total >= 91) {
        codes.push({ code: '76015', label: 'CPT 76015' });
    }

    return codes;
};

export const CptCodesView: React.FC<CptCodesViewProps> = ({
    value = [],
    sumField = 'minutes',
    label = 'Applicable CPT Codes',
    onCodesChange,
    tone = 'default',
}) => {
    const total = value.reduce(
        (sum, item) => sum + (Number(item[sumField]) || 0),
        0
    );

    const codes = calculateCptCodes(total);
    const prevCodesRef = useRef<string>('');

    // Save codes to context when they change
    useEffect(() => {
        const codesKey = JSON.stringify(codes);
        if (onCodesChange && codesKey !== prevCodesRef.current) {
            prevCodesRef.current = codesKey;
            onCodesChange(codes);
        }
    }, [codes, onCodesChange]);

    const code76014Count = codes.filter((c) => c.code === '76014').length;
    const code76015Count = codes.filter((c) => c.code === '76015').length;
    const isCptTaskLogTone = tone === CPT_TASK_LOG_TONE;

    return (
        <div className="space-y-3 rounded-xl border border-border/45 bg-background/15 p-4 dark:border-white/10 dark:bg-[rgba(17,17,17,0.52)]">
            <div className="flex justify-between items-center">
                <span
                    className={cn(
                        'text-sm font-medium text-foreground',
                        isCptTaskLogTone &&
                            'uppercase tracking-[0.18em] text-[#a5a5a5]'
                    )}
                >
                    {label}
                </span>
                <span className="text-xs text-muted-foreground">
                    Based on {total} min logged
                </span>
            </div>

            {codes.length === 0 ? (
                <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.08] px-3 py-2 text-sm italic text-amber-700 dark:text-amber-200">
                    Log at least 8 minutes to qualify for CPT codes
                </div>
            ) : (
                <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                        {codes.map((code, index) => (
                            <div
                                key={index}
                                className={cn(
                                    'rounded-md border px-3 py-1.5 text-sm font-semibold',
                                    isCptTaskLogTone
                                        ? 'border-[#315087] bg-[#1F2C49]/25 text-[#6AA2FF]'
                                        : 'border-white/12 bg-[var(--main-grey)] text-foreground'
                                )}
                            >
                                {code.label}
                            </div>
                        ))}
                    </div>
                    <div className="text-xs text-muted-foreground">
                        {code76014Count > 0 && `${code76014Count}× Code 76014`}
                        {code76014Count > 0 && code76015Count > 0 && ' + '}
                        {code76015Count > 0 && `${code76015Count}× Code 76015`}
                    </div>
                </div>
            )}
        </div>
    );
};
